import React, { useEffect, useState } from 'react';
import { Backdrop, Box, Drawer, Fade, Modal } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PinInput from 'react-pin-input';
import { useNavigate, useParams } from 'react-router-dom';
import swal from 'sweetalert';
import Loading from '../SelectValue/Loading';
import getServices from '../../Services/get-service';
import putServices from '../../Services/put-service';
import Constant from '../../Constant';
import { isFinanceManager } from '../../utils/permissions';

const drawerWidth = 620;

const pinModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'min(92vw, 400px)',
  bgcolor: 'white',
  boxShadow: 24,
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  return `NGN ${Constant.FormatNumber(value)}`;
};

const getFullName = (request) => {
  const name = `${request?.firstName || ''} ${request?.lastName || ''}`.trim();
  return name || request?.fullName || 'N/A';
};

const getInitials = (request) => {
  const name = getFullName(request);
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('');

  return initials || 'NA';
};

const getTotalPayableAmount = (request) => {
  return (
    request?.totalAmountPayable ??
    request?.totalPayableAmount ??
    request?.totalPayable ??
    request?.totalDisplayPrice ??
    request?.payableAmount ??
    request?.totalAmount ??
    request?.unitPrice ??
    null
  );
};

const DetailValue = ({ label, value }) => (
  <div>
    <p className="mb-2 text-xs uppercase text-[#4B3A6A]">{label}</p>
    <p className="text-sm font-semibold text-back-color2 break-words">{value || 'N/A'}</p>
  </div>
);

const DocumentCard = ({ label, url }) => (
  <div className="flex items-center gap-3 rounded-lg border border-[#EEE7FC] p-4">
    <InsertDriveFileOutlinedIcon className="text-primary-color" />
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-bold text-back-color2">{label}</p>
      <p className="mt-1 text-xs text-[#4B3A6A]">{url ? 'Available for review' : 'No document uploaded'}</p>
    </div>
    {url ? (
      <a href={url} target="_blank" rel="noreferrer" className="text-primary-color">
        <FileDownloadOutlinedIcon />
      </a>
    ) : null}
  </div>
);

function AssetFinancingRequestDetails({
  open,
  requestId: requestIdProp,
  onClose,
  onReviewed,
}) {
  const params = useParams();
  const navigate = useNavigate();
  const requestId = requestIdProp || params.requestId;
  const isRouteFallback = open === undefined;
  const drawerOpen = isRouteFallback ? Boolean(requestId) : open;
  const readOnly = isFinanceManager();
  const [loading, setLoading] = useState(false);
  const [request, setRequest] = useState(null);
  const [selectedAction, setSelectedAction] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [transactionPin, setTransactionPin] = useState('');
  const [pinModalOpen, setPinModalOpen] = useState(false);

  const closeDrawer = () => {
    if (loading) return;
    setSelectedAction('');
    setRejectionReason('');
    setTransactionPin('');
    setPinModalOpen(false);
    setRequest(null);

    if (onClose) {
      onClose();
      return;
    }

    navigate('/assests');
  };

  const fetchRequest = () => {
    if (!requestId || !drawerOpen) return;

    setLoading(true);
    getServices.getAssetFinancingRequest(requestId).then(
      (response) => {
        setLoading(false);
        if (response.data.status === '2000') {
          setRequest(response.data.data);
        } else {
          swal(response.data.description || 'Failed to fetch request');
        }
      },
      (error) => {
        setLoading(false);
        swal(error?.response?.data?.description || 'Error fetching request');
      }
    );
  };

  useEffect(() => {
    if (!drawerOpen) return;
    setSelectedAction('');
    setRejectionReason('');
    setTransactionPin('');
    setPinModalOpen(false);
    fetchRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen, requestId]);

  const openPinPrompt = () => {
    if (readOnly || !selectedAction) return;

    if (selectedAction === 'REJECTED' && !rejectionReason.trim()) {
      swal('Please enter reason for rejection');
      return;
    }

    setTransactionPin('');
    setPinModalOpen(true);
  };

  const closePinPrompt = () => {
    if (loading) return;
    setPinModalOpen(false);
    setTransactionPin('');
  };

  const submitReview = (event) => {
    event.preventDefault();

    if (!transactionPin || transactionPin.length !== 4) {
      swal('Enter transaction PIN');
      return;
    }

    setLoading(true);
    putServices.reviewAssetFinancingRequest(requestId, {
      decision: selectedAction,
      note: selectedAction === 'REJECTED' ? rejectionReason : '',
      transactionPin,
    }).then(
      (response) => {
        setLoading(false);
        if (response.data.status === '2000') {
          swal(response.data.description || 'Request reviewed successfully').then(() => {
            setTransactionPin('');
            setPinModalOpen(false);
            if (onReviewed) onReviewed();
            closeDrawer();
          });
        } else {
          swal(response.data.description || 'Failed to review request');
        }
      },
      (error) => {
        setLoading(false);
        swal(error?.response?.data?.description || 'Error reviewing request');
      }
    );
  };

  const totalPayableAmount = getTotalPayableAmount(request);
  const monthlyAmount = request?.monthlyRepaymentAmount ?? request?.monthlyPayment ?? null;
  const downPayment = request?.downPayment ?? request?.initialDeposit ?? null;
  const canReview = !readOnly && (request?.status || 'PENDING') === 'PENDING';

  return (
    <>
      <Drawer
        anchor="right"
        open={Boolean(drawerOpen)}
        onClose={closeDrawer}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: {
              xs: '100%',
              sm: '82%',
              md: drawerWidth,
            },
            boxSizing: 'border-box',
            backgroundColor: 'white',
          },
        }}
      >
        <div className="relative flex h-full flex-col px-5 py-6">
          <Loading open={loading} variant="local" />
          <div className="mb-6 flex items-center justify-between">
            <h1 className="text-lg font-bold text-back-color2">Review Request</h1>
            <button
              type="button"
              onClick={closeDrawer}
              className="flex h-8 w-8 items-center justify-center rounded-full text-back-color2 hover:bg-gray-100"
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>

          {!request ? (
            <div className="flex-1" />
          ) : (
            <div className="flex-1 overflow-y-auto pr-1">
              <div className="rounded-lg border border-[#EEE7FC] p-5">
                <div className="flex items-center gap-4">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-violet-500 text-2xl font-bold text-white">
                    {getInitials(request)}
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-base font-bold capitalize text-back-color2">{getFullName(request)}</h2>
                    <p className="break-words text-sm text-[#6F6780]">{request.email || 'No email'}</p>
                  </div>
                </div>

                <div className="my-5 border-t border-[#EEE7FC]" />

                <p className="mb-2 text-xs text-[#4B3A6A]">Requested Asset</p>
                <p className="mb-4 text-sm font-bold text-primary-color">
                  {request.assetName || 'N/A'} {totalPayableAmount ? `(${formatCurrency(totalPayableAmount)})` : ''}
                </p>

                <div className="mb-5 rounded-lg bg-[#F7F8FB] p-4">
                  <h3 className="mb-2 text-sm font-bold text-back-color2">Description</h3>
                  <p className="text-sm leading-6 text-[#4B3A6A]">
                    {request.assetDescription || request.requestDescription || request.purpose || 'N/A'}
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <DetailValue label="Tenure" value={`${request.tenureMonth || 0} Months`} />
                  <DetailValue label="Monthly" value={monthlyAmount ? formatCurrency(monthlyAmount) : 'N/A'} />
                  <DetailValue label="Down Payment" value={formatCurrency(downPayment)} />
                </div>
              </div>

              <div className="mt-5 rounded-lg border border-[#EEE7FC] p-5">
                <h2 className="mb-5 text-base font-bold text-back-color2">Delivery Information</h2>
                <div className="grid grid-cols-2 gap-5">
                  <DetailValue label="State/LGA" value={[request.stateOfResidence, request.lga].filter(Boolean).join(' / ')} />
                  <DetailValue label="Phone number" value={request.deliveryPhone || request.phone} />
                </div>
                <div className="mt-5">
                  <DetailValue
                    label="Street address"
                    value={request.deliveryAddress || request.address || request.cityOfResidence}
                  />
                </div>
              </div>

              <div className="mt-5">
                <h2 className="mb-4 text-base font-bold text-back-color2">Verification Documents</h2>
                <div className="space-y-3">
                  <DocumentCard label="Bank Statement" url={request.bankStatementUrl} />
                </div>
              </div>

              {canReview ? (
                <div className="mt-6 space-y-4 pb-6">
                  {!selectedAction ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setSelectedAction('APPROVED')}
                        className="w-full rounded-lg bg-violet-500 px-4 py-4 font-bold text-white hover:bg-violet-600"
                      >
                        Approve Request
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedAction('REJECTED')}
                        className="w-full rounded-lg border border-[#E4D7FB] bg-[#F8F3FF] px-4 py-4 font-bold text-primary-color hover:border-primary-color"
                      >
                        Reject Request
                      </button>
                    </>
                  ) : null}

                  {selectedAction === 'APPROVED' ? (
                    <>
                      <div className="rounded-lg bg-[#F7F8FB] p-4 text-sm text-back-color2">
                        Proceeding will mark this request as approved and notify the user.
                      </div>
                      <button
                        type="button"
                        onClick={openPinPrompt}
                        disabled={loading}
                        className="w-full rounded-lg bg-violet-500 px-4 py-4 font-bold text-white disabled:bg-gray-300"
                      >
                        Confirm approval
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedAction('')}
                        className="w-full rounded-lg border px-4 py-3 font-bold text-primary-color"
                      >
                        Back
                      </button>
                    </>
                  ) : null}

                  {selectedAction === 'REJECTED' ? (
                    <>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-back-color2">Reason for rejection</label>
                        <textarea
                          value={rejectionReason}
                          onChange={(event) => setRejectionReason(event.target.value)}
                          className="min-h-[140px] w-full resize-none rounded-lg border-0 bg-[#F7F8FB] p-4 outline-none focus:ring-1 focus:ring-primary-color"
                          placeholder="Enter reason"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={openPinPrompt}
                        disabled={loading || !rejectionReason.trim()}
                        className="w-full rounded-lg bg-violet-500 px-4 py-4 font-bold text-white disabled:bg-gray-300"
                      >
                        Confirm rejection
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedAction('');
                          setRejectionReason('');
                        }}
                        className="w-full rounded-lg border px-4 py-3 font-bold text-primary-color"
                      >
                        Back
                      </button>
                    </>
                  ) : null}
                </div>
              ) : null}

              {!canReview && !readOnly ? (
                <div className="mt-6 rounded-lg bg-[#F7F8FB] p-4 text-sm text-back-color2">
                  This request has already been reviewed.
                </div>
              ) : null}
            </div>
          )}
        </div>
      </Drawer>

      {!readOnly && (
        <Modal
          aria-labelledby="asset-review-pin-title"
          open={pinModalOpen}
          onClose={closePinPrompt}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{ timeout: 500 }}
        >
          <Fade in={pinModalOpen}>
            <Box sx={pinModalStyle} className="rounded-lg p-6">
              <div className="mb-4 flex justify-end">
                <CancelIcon
                  onClick={closePinPrompt}
                  className="cursor-pointer text-back-color2"
                  fontSize="large"
                />
              </div>
              <form onSubmit={submitReview}>
                <p id="asset-review-pin-title" className="mb-3 font-medium text-back-color2">
                  Enter your HalalWealth PIN
                </p>
                <p className="mb-5 text-[#808285]">
                  Enter your transaction PIN to confirm this review
                </p>
                <div className="flex w-full justify-center">
                  <PinInput
                    length={4}
                    initialValue={transactionPin}
                    secret
                    onChange={(value) => setTransactionPin(value)}
                    onComplete={(value) => setTransactionPin(value)}
                    type="numeric"
                    inputMode="number"
                    style={{ padding: '8px' }}
                    inputStyle={{
                      borderColor: '#00BC67',
                      backgroundColor: '#FAFBFC',
                      borderRadius: 8,
                    }}
                    inputFocusStyle={{ borderColor: '#00BC67', backgroundColor: '#CCF2E1' }}
                    autoSelect
                    regexCriteria={/^[0-9]*$/}
                  />
                </div>
                <div className="mt-6 grid grid-cols-1">
                  <button
                    type="submit"
                    disabled={loading || transactionPin.length !== 4}
                    className="rounded-lg bg-primary-color px-6 py-3 text-white disabled:bg-gray-300"
                  >
                    Submit
                  </button>
                </div>
              </form>
            </Box>
          </Fade>
        </Modal>
      )}
    </>
  );
}

export default AssetFinancingRequestDetails;
